import type { VoucherGetByCodeResponse } from '@diamond/shared/types';
import {
  buyerCurrencyFormatter,
  demagnifyQuantity,
  numberWithSeparator,
} from '@diamond/shared/utils';

/**
 * Formats a quantity for display: integer-as-is, fractional with 2 decimals,
 * plus a thousand separator.
 */
export function formatQty(value: number): string {
  const stringValue = Number.isInteger(value)
    ? String(value)
    : value.toFixed(2);
  return numberWithSeparator(stringValue);
}

/**
 * Demagnifies a 1000x-magnified quantity (used by detail endpoint's
 * `term_messages.products[].min_quantity`) and formats it for display.
 *
 * NOTE: The list endpoint (`message_rule_products.products[].min_quantity`)
 * returns the unit-form value, so use `formatQty` directly there instead.
 */
export function formatMagnifiedQty(magnified: number): string {
  const value = demagnifyQuantity(magnified) ?? 0;
  return formatQty(value);
}

/**
 * When title text embeds a magnified free-goods quantity (e.g. "Gratis 1000 …"),
 * convert it to the unit quantity shown in UI ("Gratis 1 …").
 */
function normalizeGratisRewardLine(text: string): string {
  const trimmed = text.trim();
  const match = trimmed.match(/^Gratis\s+(\d+(?:[.,]\d+)?)\s*(.*)$/i);
  if (!match) return trimmed;

  const rawQty = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(rawQty)) return trimmed;

  const displayQty =
    rawQty >= 1000 ? demagnifyQuantity(rawQty) ?? rawQty : rawQty;
  const qtyLabel = formatQty(displayQty);
  const rest = match[2]?.trim() ?? '';

  return rest ? `Gratis ${qtyLabel} ${rest}` : `Gratis ${qtyLabel}`;
}

/**
 * Last-resort fallback used when the structured detail response is missing
 * or fails. Parses the voucher title text for a reward token.
 */
export function extractRewardFromTitle(title: string): string | undefined {
  const parts = title.split('\n');
  if (parts.length > 1) {
    const lastLine = parts[parts.length - 1].trim();
    if (!lastLine) return undefined;
    if (/^Gratis\s+/i.test(lastLine)) {
      return normalizeGratisRewardLine(lastLine);
    }
    return lastLine;
  }

  const gratisMatch = title.match(/Gratis\s+.+/i);
  if (gratisMatch) {
    return normalizeGratisRewardLine(gratisMatch[0]);
  }

  return title.match(/(Diskon\s+\S+|Cashback\s+\S+)/i)?.[0]?.trim();
}

/**
 * Builds the reward text for a specific product from the structured voucher
 * detail response. Looks at `reward_products.discount_products` first, then
 * `reward_products.free_goods`, filtering by `product_item_code`.
 *
 * Returns `undefined` when no reward matches the given `itemCode` so callers
 * can fall back to `extractRewardFromTitle`.
 */
export function buildRewardForProduct(
  detail: VoucherGetByCodeResponse | undefined,
  itemCode?: number
): string | undefined {
  if (!detail || !itemCode) return undefined;

  const discount = detail.reward_products?.discount_products?.find(
    (p) => p.product_item_code === itemCode
  );
  if (discount?.discount_percentage != null) {
    return `Diskon ${discount.discount_percentage}%`;
  }
  if (discount?.discount_amount != null) {
    return `Diskon ${buyerCurrencyFormatter(discount.discount_amount)}`;
  }

  const freeGood = detail.reward_products?.free_goods?.find(
    (p) => p.product_item_code === itemCode
  );
  if (freeGood) {
    return `Gratis ${formatMagnifiedQty(freeGood.quantity)} ${freeGood.product_name}`;
  }

  return undefined;
}
